import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../utils";
import {
  useRenameDimensionItem,
  useAddChildDimensionItem,
  useAddDimensionItem,
} from "../../../hooks/query/dimensions/dimensionAdmin";

function EditDimensionItemModal({ isOpen, onClose, item, parentItem, childDimensionName, rootDimension, onSuccess }) {
  const toast = useToast();
  const isEditing = !!item;
  const isAddingRoot = !!rootDimension && !parentItem;

  const [name, setName] = useState(item?.name || "");

  const { mutate: addChildItem, isLoading: isAddingChild } = useAddChildDimensionItem({
    onSuccess: () => {
      showSuccessToast(toast, "Item created.");
      onSuccess();
      onClose();
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: addRootItem, isLoading: isAddingRootItem } = useAddDimensionItem({
    onSuccess: () => {
      showSuccessToast(toast, "Item created.");
      onSuccess();
      onClose();
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: renameItem, isLoading: isRenaming } = useRenameDimensionItem({
    onSuccess: () => {
      showSuccessToast(toast, "Item renamed.");
      onSuccess();
      onClose();
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (isEditing) {
      renameItem({ id: item.id, name: name.trim() });
    } else if (isAddingRoot) {
      addRootItem({
        dimensionId: rootDimension.id,
        data: { name: name.trim() },
      });
    } else if (parentItem) {
      addChildItem({
        parentItemId: parentItem.id,
        data: { name: name.trim() },
      });
    }
  };

  let modalTitle;
  let helpText;
  if (isEditing) {
    modalTitle = "Edit Item";
    helpText = null;
  } else if (isAddingRoot) {
    modalTitle = `Add ${rootDimension.name}`;
    helpText = `This item will be added to the ${rootDimension.name} dimension.`;
  } else if (parentItem && childDimensionName) {
    modalTitle = `Add ${childDimensionName} under "${parentItem.name}"`;
    helpText = `This item will be created in the ${childDimensionName} dimension.`;
  } else if (parentItem) {
    modalTitle = `Add child under "${parentItem.name}"`;
    helpText = null;
  } else {
    modalTitle = "Add Item";
    helpText = null;
  }

  const isSubmitting = isAddingChild || isAddingRootItem || isRenaming;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {helpText && (
                <Text fontSize="sm" color="gray.600">
                  {helpText}
                </Text>
              )}
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Melbourne, Building A" autoFocus />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" isLoading={isSubmitting} isDisabled={!name.trim()}>
              {isEditing ? "Save" : "Create"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default EditDimensionItemModal;
